<script setup lang="ts">
import { computed } from 'vue'
import { SPARKLINE_FILL, SPARKLINE_STROKE, type SparklineTone } from './app-sparkline-types'

const props = withDefaults(
  defineProps<{
    /** The series, ascending in time. Fewer than two points draws nothing. */
    values: number[]
    /** `brand` uses the accent; anything that is not the brand's own data must stay `neutral`. */
    tone?: SparklineTone
    showEndDot?: boolean
    /** Floor of the vertical scale; pass the same `min`/`max` to every sparkline in a column to make their slopes comparable. */
    min?: number
    /** Ceiling of the vertical scale. See {@link min}. */
    max?: number
    /** Stretches to the width of its container instead of the fixed small width. */
    fill?: boolean
  }>(),
  { tone: 'brand', showEndDot: false, min: undefined, max: undefined, fill: false },
)

const VB_W = 64
const VB_H = 24
const PAD = 3

const points = computed(() => {
  const values = props.values
  if (values.length < 2) return []

  const lo = props.min ?? Math.min(...values)
  const hi = props.max ?? Math.max(...values)
  const span = hi - lo
  const plotHeight = VB_H - PAD * 2

  return values.map((value, index) => ({
    x: (index / (values.length - 1)) * VB_W,
    y:
      span === 0
        ? VB_H / 2
        : VB_H - PAD - Math.min(1, Math.max(0, (value - lo) / span)) * plotHeight,
  }))
})

const polyline = computed(() => points.value.map((point) => `${point.x},${point.y}`).join(' '))
const end = computed(() => points.value[points.value.length - 1] ?? null)
</script>

<template>
  <svg
    v-if="points.length > 0"
    :viewBox="`0 0 ${VB_W} ${VB_H}`"
    preserveAspectRatio="none"
    aria-hidden="true"
    class="h-12"
    :class="fill ? 'w-full min-w-0' : 'w-32 shrink-0'"
  >
    <polyline
      :points="polyline"
      fill="none"
      :class="SPARKLINE_STROKE[tone]"
      stroke-width="1.5"
      stroke-linejoin="round"
      stroke-linecap="round"
      vector-effect="non-scaling-stroke"
    />
    <circle
      v-if="showEndDot && end"
      :cx="end.x"
      :cy="end.y"
      r="2"
      :class="SPARKLINE_FILL[tone]"
      vector-effect="non-scaling-stroke"
    />
  </svg>
</template>
